/**
 * Whether this user has seen the latest release, and the "What's new" modal
 * that tells them about it (ported from marq-los-frontend).
 *
 * SUPER ADMINS ONLY for now: for everyone else this renders its children and
 * nothing more, and the modal itself says only super admins can see it.
 *
 * A deployment that adds a release to the top of changelogData ships a new
 * `version`; the first authenticated page load after it opens the modal (half
 * a second in, so the dashboard paints first). Closing it any way (Dismiss, ×,
 * Esc, the backdrop, a row, View all updates) or opening the Changelog page
 * marks that release seen.
 *
 * Seen is stored per user in partner-scoped localStorage, so it is per
 * browser: a second device shows the modal once there too.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation } from "react-router-dom";

import { useAuth } from "../../auth/AuthProvider";
import { usePermissionsHook } from "../../hooks/usePermissionsHook";
import { partnerStorage } from "../../utils/storage/partnerStorage";
import CHANGELOG from "./changelogData";
import WhatsNewModal from "./WhatsNewModal";

const OPEN_DELAY_MS = 500;
const CHANGELOG_PATH = "/changelog";
const storageKey = (userKey) => `whatsNew.seenRelease.${userKey}`;
// `reviewRevision` on a release re-opens the review for everyone who already
// dismissed it (bump it after changing an already-deployed release).
const releaseKey = (rel) =>
  rel
    ? String(rel.version || rel.date || "") +
      (rel.reviewRevision ? `#${rel.reviewRevision}` : "")
    : "";

const readSeen = (userKey) => {
  try {
    return partnerStorage.getItem(storageKey(userKey));
  } catch {
    return null;
  }
};

const WhatsNewContext = createContext({
  canView: false,
  openWhatsNew: () => {},
});

const WhatsNewProvider = ({ children }) => {
  const { user } = useAuth();
  const {
    isSuperAdmin,
    isAdmin,
    isCapitalMarkets,
    isCloser,
    isUnderwriter,
  } = usePermissionsHook();
  const { pathname } = useLocation();
  const userKey = isSuperAdmin ? user?.email || user?.id || null : null;

  // Every release this user may see, each with only the changes they may see
  // (same role gating as the Changelog page). The first is the latest.
  const visible = useMemo(() => {
    const flags = {
      admin: isAdmin,
      capitalMarkets: isCapitalMarkets,
      closer: isCloser,
      underwriter: isUnderwriter,
    };
    const allowed = (roles) =>
      !Array.isArray(roles) || !roles.length || roles.some((r) => flags[r]);
    return (
      (Array.isArray(CHANGELOG) ? CHANGELOG : [])
        .filter((rel) => allowed(rel.roles))
        .map((rel) => ({
          ...rel,
          visibleChanges: (rel.changes || []).filter((c) => allowed(c.roles)),
        }))
        .filter((rel) => rel.visibleChanges.length > 0)
    );
  }, [isAdmin, isCapitalMarkets, isCloser, isUnderwriter]);
  const latest = visible[0] || null;
  const latestKey = releaseKey(latest);

  const [seenKey, setSeenKey] = useState(() =>
    userKey ? readSeen(userKey) : null,
  );
  useEffect(() => {
    setSeenKey(userKey ? readSeen(userKey) : null);
  }, [userKey]);

  const unseen = !!(userKey && latestKey && seenKey !== latestKey);

  const markSeen = useCallback(() => {
    if (!userKey || !latestKey) return;
    try {
      partnerStorage.setItem(storageKey(userKey), latestKey);
    } catch {
      // Storage blocked (private mode): the modal shows again next load.
    }
    setSeenKey(latestKey);
  }, [userKey, latestKey]);

  const [open, setOpen] = useState(false);
  // The release shown when opened on demand from the Changelog page's
  // "What's new (admin only)" button; otherwise the latest.
  const [shown, setShown] = useState(null);
  const onChangelog = pathname.startsWith(CHANGELOG_PATH);

  // Reading the Changelog page counts as seeing the release.
  useEffect(() => {
    if (unseen && onChangelog) markSeen();
  }, [unseen, onChangelog, markSeen]);

  // Once per release, a beat after the page renders. Never on the Changelog
  // page itself.
  useEffect(() => {
    if (!unseen || onChangelog) return undefined;
    const t = setTimeout(() => setOpen(true), OPEN_DELAY_MS);
    return () => clearTimeout(t);
  }, [unseen, onChangelog]);

  const close = useCallback(() => {
    setOpen(false);
    markSeen();
  }, [markSeen]);

  const openWhatsNew = useCallback(
    (rel) => {
      const key = releaseKey(rel);
      setShown(visible.find((r) => releaseKey(r) === key) || null);
      setOpen(true);
    },
    [visible],
  );

  const value = useMemo(
    () => ({ canView: isSuperAdmin && !!latest, openWhatsNew }),
    [isSuperAdmin, latest, openWhatsNew],
  );

  return (
    <WhatsNewContext.Provider value={value}>
      {children}
      {isSuperAdmin && latest ? (
        <WhatsNewModal
          open={open}
          release={shown || latest}
          onClose={close}
          // Cleared only once the exit transition ends, so the modal does not
          // swap releases while it fades out.
          onExited={() => setShown(null)}
        />
      ) : null}
    </WhatsNewContext.Provider>
  );
};

/** `canView` (super admins only) and `openWhatsNew(release?)`. */
export const useWhatsNew = () => useContext(WhatsNewContext);

export default WhatsNewProvider;
